import { useEffect, useState } from "react";
import { Loader2, CheckCircle2, Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { opsApi } from "../../lib/api";

/** SUB SECTION 5 — Submit All follow-up. The email itself opens in the user's own mail client
 *  (sent from their address, ZIP attached by hand); this prompt records that it went. */
export default function SubmitAllDialog({ projectId, draft, onClose, onSent }) {
  const [busy, setBusy] = useState(false);
  const packages = draft.packages || [];

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [busy, onClose]);

  const markSent = async () => {
    setBusy(true);
    try {
      await Promise.all(packages.map((p) => opsApi.submittalMarkSubmitted(projectId, p.id,
        { note: "sent from the user's own email via Submit all" })));
      toast.success(`${packages.length} package(s) marked submitted`);
      onSent?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not mark those packages submitted");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
         onClick={onClose}>
      <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl"
           onClick={(e) => e.stopPropagation()} data-testid="ops-submit-all-dialog">
        <h3 className="text-base font-semibold text-slate-900">Did you send it?</h3>
        <p className="mt-1 text-xs text-slate-500">
          Your email opened with the {draft.audience_label || "recipients"} filled in.
          Attach the downloaded ZIP, send it, then mark these packages as submitted so the
          due-back clock starts.
        </p>
        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600"
             data-testid="ops-submit-all-attachments">
          <p className="flex items-center gap-1 font-medium text-slate-700">
            <Paperclip className="h-3 w-3" /> In the ZIP
          </p>
          <ul className="mt-1 space-y-0.5">
            {packages.map((p) => <li key={p.id}>{p.number} — {p.title || "submittal"}</li>)}
          </ul>
        </div>
        <div className="mt-4 flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}
                  data-testid="ops-submit-all-not-yet">Not yet</Button>
          <Button variant="data" size="sm" onClick={markSent} disabled={busy} data-testid="ops-submit-all-send">
            {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              : <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />} Mark as submitted
          </Button>
        </div>
      </div>
    </div>
  );
}
